import { TableAbstractExternalSyncStorage } from './TableAbstractExternalSyncStorage';
import { PositionsProviderEventType } from '../../cache/Positions/IPositionsProvider.interface';
import { getFormattedValue, getValue } from '../../Table/Positions/PositionTableData';
import { RowType } from '../../Table/TableRow.interface';
export class PositionsStorage extends TableAbstractExternalSyncStorage {
    get isDisplayTrades() {
        return this._isDisplayTrades;
    }
    set isDisplayTrades(value) {
        if (this._isDisplayTrades === value) {
            return;
        }
        this._isDisplayTrades = value;
        this.initialize();
    }
    constructor(positionsProvider, appSettings) {
        super();
        this._tradesWithoutParentPosition = [];
        this._isDisplayTrades = false;
        this.getPositionOrCorporateAction = (row) => {
            if (isNullOrUndefined(row)) {
                return null;
            }
            const id = row.id;
            let positionOrCorporateAction = this._positionsProvider.getPositionById(id);
            if (isNullOrUndefined(positionOrCorporateAction)) {
                positionOrCorporateAction = this._positionsProvider.getPositionsCorporateActionById(id);
            }
            return positionOrCorporateAction !== null && positionOrCorporateAction !== void 0 ? positionOrCorporateAction : null;
        };
        this.updateTotals = () => {
            this.notifyTableListeners();
        };
        this.onAddPosition = (position) => {
            if (position.getIsTradePosition()) {
                this.addTradeRow(position);
            }
            else {
                this.addPositionRow(position);
            }
        };
        this.onRemovePosition = (position) => {
            if (position.getIsTradePosition()) {
                this.removeTradeRow(position);
            }
            else {
                this.removePositionRow(position);
            }
        };
        this.onUpdatePosition = (position) => {
            if (position.getIsTradePosition()) {
                this.updateTradeRow(position);
            }
            else {
                this.updatePositionRow(position);
            }
        };
        this.addPositionRow = (position) => {
            this.addRow(this.createRow(position));
            this.processTradesWithoutParentPosition();
        };
        this.removePositionRow = (position) => {
            const row = this.getRow(position.getPositionId());
            if (!isNullOrUndefined(row)) {
                this.removeRow(row);
            }
        };
        this.updatePositionRow = (position) => {
            const row = this.getRow(position.getPositionId());
            if (!isNullOrUndefined(row)) {
                row.disabled = position.isPendingExerciseOptionStatus();
                this.updateRow(row);
            }
        };
        this.addTradeRow = (trade) => {
            if (this.isDisplayTrades === false) {
                return;
            }
            const snapshot = this.getSnapshot();
            if (this.tryCreateTradeRow(snapshot.rows, trade) === false) {
                this._tradesWithoutParentPosition.push(trade);
            }
            else {
                this.updateRows();
            }
        };
        this.removeTradeRow = (trade) => {
            const pendingIndex = this._tradesWithoutParentPosition.findIndex((pending) => pending.getPositionId() === trade.getPositionId());
            if (pendingIndex !== -1) {
                this._tradesWithoutParentPosition.splice(pendingIndex, 1);
            }
            if (this.isDisplayTrades === false) {
                return;
            }
            const parentRow = this.getRow(trade.getSuperPositionId());
            if (isNullOrUndefined(parentRow)) {
                return;
            }
            const childIndex = parentRow.children.findIndex((child) => child.id === trade.getPositionId());
            if (childIndex === -1) {
                return;
            }
            parentRow.children.splice(childIndex, 1);
            this.updateRows();
        };
        this.updateTradeRow = (trade) => {
            if (this.isDisplayTrades === false) {
                return;
            }
            const parentRow = this.getRow(trade.getSuperPositionId());
            if (isNullOrUndefined(parentRow)) {
                return;
            }
            const childIndex = parentRow.children.findIndex((child) => child.id === trade.getPositionId());
            if (childIndex === -1) {
                return;
            }
            const child = parentRow.children[childIndex];
            child.disabled = trade.isPendingExerciseOptionStatus();
            this.updateRow(child);
        };
        this.onUpdateAppSettings = () => {
            this.notifyRowsListeners();
            this.notifyTableListeners();
        };
        this._positionsProvider = positionsProvider;
        this._appSettingsProvider = appSettings;
    }
    onSubscribe() {
        this.initialize();
        this._appSettingsProvider.subscribeOnChanged(this.onUpdateAppSettings);
        this._positionsProvider.subscribePositionsEvent(PositionsProviderEventType.AddPosition, this.onAddPosition);
        this._positionsProvider.subscribePositionsEvent(PositionsProviderEventType.RemovePosition, this.onRemovePosition);
        this._positionsProvider.subscribePositionsEvent(PositionsProviderEventType.UpdatePosition, this.onUpdatePosition);
        this._positionsProvider.subscribePositionsEvent(PositionsProviderEventType.AddCorporateAction, this.onAddPosition);
        this._positionsProvider.subscribePositionsEvent(PositionsProviderEventType.RemoveCorporateAction, this.onRemovePosition);
        this._positionsProvider.subscribePositionsEvent(PositionsProviderEventType.UpdateCorporateAction, this.onUpdatePosition);
    }
    onUnsubscribe() {
        this._appSettingsProvider.unsubscribeOnChanged(this.onUpdateAppSettings);
        this._positionsProvider.unsubscribePositionsEvent(PositionsProviderEventType.AddPosition, this.onAddPosition);
        this._positionsProvider.unsubscribePositionsEvent(PositionsProviderEventType.RemovePosition, this.onRemovePosition);
        this._positionsProvider.unsubscribePositionsEvent(PositionsProviderEventType.UpdatePosition, this.onUpdatePosition);
        this._positionsProvider.unsubscribePositionsEvent(PositionsProviderEventType.AddCorporateAction, this.onAddPosition);
        this._positionsProvider.unsubscribePositionsEvent(PositionsProviderEventType.RemoveCorporateAction, this.onRemovePosition);
        this._positionsProvider.unsubscribePositionsEvent(PositionsProviderEventType.UpdateCorporateAction, this.onUpdatePosition);
        this.clearRows();
    }
    addRow(row) {
        const position = this.getPositionOrCorporateAction(row);
        if (position !== null) {
            position.subscribeQuoteUpdate(this.onUpdatePosition);
        }
        super.addRow(row);
    }
    removeRow(row) {
        const position = this.getPositionOrCorporateAction(row);
        if (position !== null) {
            position.unsubscribeQuoteUpdate(this.onUpdatePosition);
        }
        super.removeRow(row);
    }
    initializeRows(rows) {
        for (const row of rows) {
            const position = this.getPositionOrCorporateAction(row);
            if (position !== null) {
                position.subscribeQuoteUpdate(this.onUpdatePosition);
            }
        }
        super.initializeRows(rows);
    }
    clearRows() {
        for (const row of this.getSnapshot().rows) {
            const position = this.getPositionOrCorporateAction(row);
            if (position !== null) {
                position.unsubscribeQuoteUpdate(this.onUpdatePosition);
            }
        }
        this._tradesWithoutParentPosition.length = 0;
        super.clearRows();
    }
    createRow(position) {
        return {
            id: position.getPositionId(),
            type: RowType.Row,
            data: {
                getValue: (columnId) => getValue(position, columnId, this._appSettingsProvider),
                getFormattedValue: (columnId) => getFormattedValue(position, columnId, this._appSettingsProvider)
            },
            disabled: position.isPendingExerciseOptionStatus(),
            children: []
        };
    }
    tryCreateTradeRow(rows, trade) {
        if (this.isDisplayTrades === false) {
            return true;
        }
        const positionRow = rows.find((row) => row.id === trade.getSuperPositionId());
        if (positionRow) {
            positionRow.children.push(this.createRow(trade));
            return true;
        }
        return false;
    }
    initialize() {
        this.clearRows();
        const positionRows = [];
        const trades = [];
        const positionsAndCorporateActions = [
            ...this._positionsProvider.getAllPositions(),
            ...this._positionsProvider.getAllPositionsCorporateAction()
        ];
        for (const position of positionsAndCorporateActions) {
            if (position.getIsTradePosition()) {
                trades.push(position);
            }
            else {
                positionRows.push(this.createRow(position));
            }
        }
        for (const trade of trades) {
            if (this.tryCreateTradeRow(positionRows, trade) === false) {
                this._tradesWithoutParentPosition.push(trade);
            }
        }
        this.initializeRows(positionRows);
    }
    processTradesWithoutParentPosition() {
        if (this.isDisplayTrades === false) {
            return;
        }
        if (this._tradesWithoutParentPosition.length === 0) {
            return;
        }
        const createdTrades = [];
        for (const trade of this._tradesWithoutParentPosition) {
            if (this.tryCreateTradeRow(this.getSnapshot().rows, trade) === true) {
                createdTrades.push(trade);
            }
        }
        if (createdTrades.length > 0) {
            for (const trade of createdTrades) {
                const pendingIndex = this._tradesWithoutParentPosition.findIndex((pending) => pending.getPositionId() === trade.getPositionId());
                if (pendingIndex !== -1) {
                    this._tradesWithoutParentPosition.splice(pendingIndex, 1);
                }
            }
            this.updateRows();
        }
    }
}
